import { FetchError } from "common/fetch/types";
import { Optional } from "common/utils/types";
import { flattenAllErrors } from "common/utils/errors";

import Alert, { ActionButton } from "../content/Alert";

import styles from "../../styles/FetchErrorAlert.module.less";

export type FetchErrorAlertFieldErrors = Optional<(string[] | undefined)[]>;

export type FetchErrorAlertProps<T> = {
  error: Optional<FetchError<T>>;
  fieldErrors?: FetchErrorAlertFieldErrors;
  actions?: ActionButton[];
  displayAllErrors?: boolean;
};

/**
 * Alert to display if we have detected a fetch error.
 */
const FetchErrorAlert = <T extends object>({
  error,
  fieldErrors = [],
  actions = [],
  displayAllErrors = false,
}: FetchErrorAlertProps<T>) => {
  // This is just a sanity check to prevent rendering the alert if there is no error.
  if (!error) {
    return null;
  }

  // deduplicated errors combining field errors, non field errors, and `fieldErrors`
  const errors = displayAllErrors
    ? flattenAllErrors(error?.errors)
    : (error.errors?.non_field_errors ?? []);
  const displayErrors = [
    ...new Set([...errors, ...(fieldErrors ?? [])].flatMap((fieldError) => fieldError ?? [])),
  ];

  return (
    <div>
      <Alert variant="danger" actions={actions} shouldScrollOnMount={true}>
        <>
          <div className={styles.title}>{error?.detail || `Something went wrong.`}</div>
          {displayErrors.length > 0 && (
            <div className={styles.list}>
              {displayErrors.map((msg) => (
                <li key={msg} className={styles.item}>
                  {msg}
                </li>
              ))}
            </div>
          )}
        </>
      </Alert>
    </div>
  );
};

export default FetchErrorAlert;
